import {disableGoogleAnalytics, initializeGoogleAnalytics} from '../google-analytics';
import {isNativeCapacitorPlatform} from '../platform';
import type {
  WebAnalyticsConsentChoice,
  WebAnalyticsConsentRecord,
  WebAnalyticsConsentState,
} from './web-analytics-consent.models';
import {resolveWebAnalyticsRegionPolicy} from './web-analytics-region-policy';

export const WEB_ANALYTICS_CONSENT_STORAGE_KEY = 'invemory-web-analytics-consent';
export const WEB_ANALYTICS_CONSENT_POLICY_VERSION = 1;

type StateListener = (state: WebAnalyticsConsentState) => void;

function initialState(): WebAnalyticsConsentState {
  return {
    choice: null,
    collectionEnabled: false,
    policy: 'unknown',
    policySource: 'unavailable',
    promptOpen: false,
    settingsOpen: false,
    storageAvailable: false,
  };
}

export class WebAnalyticsConsentCoordinator {
  private state = initialState();
  private readonly listeners = new Set<StateListener>();
  private initialized = false;
  private storageListenerInstalled = false;

  constructor(
    private readonly globalScope: typeof globalThis = globalThis,
    private readonly documentScope: Document | null = typeof document === 'undefined' ? null : document,
  ) {}

  initialize(): WebAnalyticsConsentState {
    if (this.initialized) {
      return this.getState();
    }
    this.initialized = true;

    if (!this.documentScope || isNativeCapacitorPlatform()) {
      return this.getState();
    }

    let region;
    try {
      region = resolveWebAnalyticsRegionPolicy(this.globalScope, this.documentScope);
    } catch {
      region = {countryCode: null, policy: 'unknown', source: 'unavailable'} as const;
    }
    const readResult = this.readConsentRecord();
    const choice = readResult.record?.choice ?? null;
    const policy = readResult.storageAvailable ? region.policy : 'unknown';
    const shouldEnable = choice === 'granted' || (choice === null && policy === 'defaultOn');

    this.state = {
      choice,
      collectionEnabled: shouldEnable ? this.enableAnalytics() : false,
      policy,
      policySource: region.source,
      promptOpen: choice === null && policy !== 'defaultOn',
      settingsOpen: false,
      storageAvailable: readResult.storageAvailable,
    };

    if (!shouldEnable) {
      this.disableAnalytics();
    }
    this.installStorageListener();
    return this.getState();
  }

  getState(): WebAnalyticsConsentState {
    return {...this.state};
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  openSettings(): void {
    this.initialize();
    if (!this.documentScope || isNativeCapacitorPlatform()) {
      return;
    }
    this.patchState({promptOpen: true, settingsOpen: true});
  }

  choose(choice: WebAnalyticsConsentChoice): boolean {
    this.initialize();
    const stored = this.writeConsentRecord(choice);
    const granted = choice === 'granted' && stored;
    const collectionEnabled = granted
      ? this.enableAnalytics()
      : false;

    if (!granted) {
      this.disableAnalytics();
    }
    this.patchState({
      choice: stored || choice === 'denied' ? choice : null,
      collectionEnabled,
      promptOpen: false,
      settingsOpen: false,
      storageAvailable: this.state.storageAvailable && stored,
    });
    return collectionEnabled;
  }

  private readConsentRecord(): {record: WebAnalyticsConsentRecord | null; storageAvailable: boolean} {
    let rawValue: string | null;
    try {
      rawValue = this.globalScope.localStorage.getItem(WEB_ANALYTICS_CONSENT_STORAGE_KEY);
    } catch {
      return {record: null, storageAvailable: false};
    }

    if (rawValue === null) {
      return {record: null, storageAvailable: true};
    }

    try {
      const record = JSON.parse(rawValue) as Partial<WebAnalyticsConsentRecord>;
      if (
        (record.choice !== 'granted' && record.choice !== 'denied')
        || record.policyVersion !== WEB_ANALYTICS_CONSENT_POLICY_VERSION
      ) {
        return {record: null, storageAvailable: true};
      }
      return {record: record as WebAnalyticsConsentRecord, storageAvailable: true};
    } catch {
      return {record: null, storageAvailable: true};
    }
  }

  private writeConsentRecord(choice: WebAnalyticsConsentChoice): boolean {
    try {
      const record: WebAnalyticsConsentRecord = {
        choice,
        policyVersion: WEB_ANALYTICS_CONSENT_POLICY_VERSION,
        decidedAt: new Date().toISOString(),
      };
      this.globalScope.localStorage.setItem(WEB_ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(record));
      return true;
    } catch {
      return false;
    }
  }

  private installStorageListener(): void {
    if (this.storageListenerInstalled || typeof this.globalScope.addEventListener !== 'function') {
      return;
    }
    this.storageListenerInstalled = true;
    try {
      this.globalScope.addEventListener('storage', (event: StorageEvent) => {
        if (event.key !== WEB_ANALYTICS_CONSENT_STORAGE_KEY) {
          return;
        }
        const readResult = this.readConsentRecord();
        const choice = readResult.record?.choice ?? null;
        const shouldEnable = readResult.storageAvailable
          && (choice === 'granted' || (choice === null && this.state.policy === 'defaultOn'));
        const collectionEnabled = shouldEnable ? this.enableAnalytics() : false;
        if (!collectionEnabled) {
          this.disableAnalytics();
        }
        this.patchState({
          choice,
          collectionEnabled,
          promptOpen: choice === null && this.state.policy !== 'defaultOn',
          settingsOpen: false,
          storageAvailable: readResult.storageAvailable,
        });
      });
    } catch {
      // Cross-tab synchronization is best-effort and must not interrupt startup.
    }
  }

  private enableAnalytics(): boolean {
    try {
      return initializeGoogleAnalytics(this.globalScope, this.documentScope);
    } catch {
      this.disableAnalytics();
      return false;
    }
  }

  private disableAnalytics(): void {
    try {
      disableGoogleAnalytics(this.globalScope, this.documentScope);
    } catch {
      // Analytics and cookie cleanup failures must never interrupt Invemory.
    }
  }

  private patchState(patch: Partial<WebAnalyticsConsentState>): void {
    this.state = {...this.state, ...patch};
    for (const listener of this.listeners) {
      listener(this.getState());
    }
  }
}

export const webAnalyticsConsentCoordinator = new WebAnalyticsConsentCoordinator();

export function initializeWebAnalyticsConsent(): WebAnalyticsConsentState {
  return webAnalyticsConsentCoordinator.initialize();
}
